/**
 * Ported from the Ahead Design System (`design-system/components/ui/button.tsx`)
 * so the booking flow's CTAs share styling with marketing-site, report-app, etc.
 * Self-contained — only imports `cn` from `@/lib/utils` and inlines the DS
 * arrow icon so we don't need to pull in the whole icons module.
 *
 * Nudge animation pattern (per DS prompts/per-component/button.md):
 *   <Button variant="primary" className="group" iconRight={<NudgeArrow />}>
 *     Continue
 *   </Button>
 *
 * The `group` class on the button + `group-hover:translate-x-1` on the arrow
 * (encapsulated in `<NudgeArrow />`) is the canonical hover treatment.
 */

import { forwardRef } from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

export function ArrowRightIcon({
  className,
  size = 20,
}: {
  className?: string;
  size?: number;
}) {
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M13.8563 6.23867C14.1536 5.92044 14.6354 5.92045 14.9326 6.23867L19.7771 11.4228C20.0743 11.741 20.0743 12.259 19.7771 12.5772L14.9326 17.7613C14.6354 18.0796 14.1536 18.0796 13.8563 17.7613C13.5591 17.4431 13.5591 16.9273 13.8563 16.6091L17.4005 12.8149L4.81494 12.8149C4.36484 12.8149 4 12.45 4 12C4 11.55 4.36484 11.1851 4.81494 11.1851L17.4005 11.1851L13.8563 7.39093C13.5591 7.07271 13.5591 6.55689 13.8563 6.23867Z"
        fill="currentColor"
      />
    </svg>
  );
}

/**
 * Convenience: the arrow icon pre-wrapped with the DS nudge classes. Pair
 * with `className="group"` on the surrounding Button / anchor.
 */
export function NudgeArrow({ size = 20 }: { size?: number }) {
  return (
    <ArrowRightIcon
      size={size}
      className="transition-transform duration-200 group-hover:translate-x-1"
    />
  );
}

export const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap font-medium transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-aubergine-400 disabled:pointer-events-none disabled:cursor-not-allowed cursor-pointer text-[16px] leading-[24px] tracking-[0.15px] normal-case",
  {
    variants: {
      variant: {
        primary:
          "text-white bg-aubergine-800 hover:shadow-[inset_0_0_0_999px_rgba(255,255,255,0.08)] disabled:bg-neutral-200 disabled:text-neutral-400",
        ink: "text-white bg-neutral-950 hover:shadow-[inset_0_0_0_999px_rgba(255,255,255,0.08)] disabled:bg-neutral-200 disabled:text-neutral-400",
        "primary-alt":
          "text-white bg-[#102342] hover:shadow-[inset_0_0_0_999px_rgba(255,255,255,0.08)] disabled:bg-neutral-200 disabled:text-neutral-400",
        tonal:
          "text-neutral-900 bg-neutral-100 hover:shadow-[inset_0_0_0_999px_rgba(0,0,0,0.03)] disabled:bg-neutral-100/60 disabled:text-neutral-400",
        outline:
          "text-neutral-900 bg-transparent border border-neutral-300 hover:bg-neutral-900/5 hover:border-neutral-300 disabled:bg-neutral-900/5 disabled:text-neutral-400 disabled:border-neutral-300/60",
        text: "text-neutral-900 bg-transparent hover:bg-white/10 disabled:text-neutral-400",
      },
      shape: {
        round: "rounded-full",
        square: "rounded-[12px]",
      },
      size: {
        default: "h-[44px] px-8 py-[10px]",
        sm: "h-[36px] px-4 py-1.5 text-[14px] leading-[20px]",
        lg: "h-[52px] px-10 py-3 text-[16px] leading-[24px]",
      },
    },
    defaultVariants: { variant: "primary", shape: "round", size: "default" },
  },
);

export interface DsButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  iconLeft?: React.ReactNode;
  iconRight?: React.ReactNode;
}

export const DsButton = forwardRef<HTMLButtonElement, DsButtonProps>(
  (
    {
      className,
      variant,
      shape,
      size,
      iconLeft,
      iconRight,
      children,
      ...props
    },
    ref,
  ) => {
    return (
      <button
        className={cn(
          buttonVariants({ variant, shape, size }),
          iconRight && !iconLeft && "pr-6",
          iconLeft && !iconRight && "pl-6",
          className,
        )}
        ref={ref}
        {...props}
      >
        {iconLeft}
        {children}
        {iconRight}
      </button>
    );
  },
);
DsButton.displayName = "DsButton";
